import type { GeneratedPositionAnnotations } from "@repo/shared/types";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./position-note-badges.i18n";

type PositionNoteBadgesProps = {
  annotations: GeneratedPositionAnnotations | null;
};

/**
 * The notes of one generated position: a single sustainability conflict
 * however many failed criteria are stored (every one carries the same fixed
 * label), the base products it replaces (QL-584), the "not suitable, no
 * replacement" flag and the client preferences it serves. Its tooltips use
 * the app-root TooltipProvider.
 */
export function PositionNoteBadges({ annotations }: PositionNoteBadgesProps) {
  const t = useT(T);
  const localize = useLocalize();
  if (!annotations) return null;

  return (
    <>
      {annotations.failedCriteria.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="warning" className="text-xs ml-2 cursor-help">
              {t.sustainabilityConflictBadge}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            {t.sustainabilityConflictTooltip}
          </TooltipContent>
        </Tooltip>
      )}
      {(annotations.replaces ?? []).map((replaced) => {
        const product = localize(replaced.productNameI18n);
        return (
          <Tooltip key={replaced.productId}>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="text-xs ml-2 cursor-help">
                {(replaced.partial ? t.partiallyReplacesBadge : t.replacesBadge)(product)}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              {(replaced.partial ? t.partiallyReplacesTooltip : t.replacesTooltip)(product)}
              {replaced.alternatives.length > 0 &&
                ` ${t.replacesAlternatives(
                  replaced.alternatives
                    .map((alternative) => localize(alternative.productNameI18n))
                    .join(", "),
                )}`}
            </TooltipContent>
          </Tooltip>
        );
      })}
      {annotations.unsuitableNoReplacement && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="destructive" className="text-xs ml-2 cursor-help">
              {t.unsuitableBadge}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            {t.unsuitableTooltip}
          </TooltipContent>
        </Tooltip>
      )}
      {(annotations.preferences ?? []).map((served) => {
        const preference = localize(served.preferenceNameI18n);
        return (
          <Tooltip key={served.preferenceId}>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="text-xs ml-2 cursor-help">
                {t.preferenceBadge(preference)}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              {t.preferenceTooltip(preference)}
              {served.alternatives.length > 0 &&
                ` ${t.preferenceAlternatives(
                  served.alternatives
                    .map((alternative) => localize(alternative.productNameI18n))
                    .join(", "),
                )}`}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </>
  );
}
